import { type JSX } from "react";
import {
  Image,
  RichText,
  Text,
  type Field,
  type ImageField,
  type LinkField,
  type RichTextField,
} from "@sitecore-content-sdk/nextjs";
import Button from "../../atoms/Button";
import VideoIframe from "../../atoms/VideoIframe";
import RichTextWithTable from "../RichTextWithTable/RichTextWithTables";
import { extractHiddenBlock } from "../../utils/styles";
import styles from "./Header.module.scss";

interface HeaderProps {
  id?: string;
  title?: Field<string>;
  subtitle?: Field<string>;
  body?: Field<string> | RichTextField;
  image?: ImageField;
  youtubeUrl?: Field<string>;
  primaryCta?: LinkField;
  secondaryCta?: LinkField;
  whiteText?: boolean;
  styleProps?: string;
  gridProps?: string;
  componentName?: string;
  isPageEditing?: boolean;
  isPreviewMode?: boolean;
}

const getContainerClass = (
  styleProps: string,
  hiddenBlockStyles: string,
  componentName: string,
) =>
  [
    styles.header,
    "px-[var(--spacing-sixteen)] pt-5",
    styleProps?.includes("mb-0") ? "mb-0" : "mb-[56px]",
    !styleProps?.includes("no-container") && componentName === "WelcomeModule"
      ? "max-w-[1440px] mx-auto"
      : "",
    styleProps,
    hiddenBlockStyles,
  ]
    .filter(Boolean)
    .join(" ");

const Header = ({
  id,
  title,
  subtitle,
  body,
  image,
  youtubeUrl,
  primaryCta,
  secondaryCta,
  whiteText,
  styleProps = "",
  gridProps = "",
  componentName = "",
  isPageEditing = false,
  isPreviewMode = false,
}: HeaderProps): JSX.Element => {
  const hasVideo = !!youtubeUrl?.value;
  const hasImage = !!image?.value?.src;

  const getMediaContent = () => {
    if (hasVideo) {
      return (
        <VideoIframe
          src={youtubeUrl?.value || ""}
          title={title?.value ? String(title.value) : "Video"}
          ariaLabel={`YouTube video: ${title?.value}`}
          data-testid="youtube-video"
          isPreviewMode={isPreviewMode}
        />
      );
    }

    if (hasImage) {
      return (
        <Image
          field={image}
          className="w-full object-cover min-h-[310px]"
          data-testid="Image"
          imageParams={{ mw: 1200 }}
        />
      );
    }
    return null;
  };

  const mediaContent = getMediaContent();

  const hiddenBlockStyles = extractHiddenBlock(gridProps);
  const containerClass = getContainerClass(
    styleProps,
    hiddenBlockStyles,
    componentName,
  );

  const hasMediaSlot = isPageEditing || mediaContent;

  return (
    <div
      className={containerClass}
      id={id || undefined}
      data-testid="header-component"
    >
      <div className="flex flex-col md:flex-row items-start">
        <div
          className={`${hasMediaSlot ? "md:w-1/2 lg:w-1/3" : ""} w-full mb-4 md:mb-0 md:pr-4 [&:empty]:hidden`}
        >
          {isPageEditing ? (
            <>
              <Text field={youtubeUrl} tag="p" className="mb-5" />
              <Image
                field={image}
                className="w-full object-cover"
                data-testid="Image"
                imageParams={{ mw: 1200 }}
              />
            </>
          ) : (
            mediaContent
          )}
        </div>

        <div
          className={`${hasMediaSlot ? "md:w-1/2 lg:w-2/3" : "w-full"} text-left pb-0`}
        >
          <div className={hasMediaSlot ? "md:pl-2" : ""}>
            {(title || isPageEditing) && (
              <RichText
                tag="h1"
                className={`${styles.headerTitle} richtext-content mt-0 mb-5 relative text-[34px] lg:text-[42px] !leading-[42.16px] lg:!leading-[52.08px] tracking-[0] font-medium pr-[var(--spacing-sixteen)] pb-[15px] ${
                  whiteText ? "text-white" : "text-ecolab-blue"
                } lg:w-[72%]`}
                field={title}
                data-testid="header-title"
              />
            )}
            {(subtitle || isPageEditing) && (
              <RichText
                tag="h2"
                className={`${styles.subTitle} richtext-content z-10 pb-[20px] text-xl leading-[1.5] tracking-[0.4px] m-0 font-medium pr-[var(--spacing-sixteen)] ${
                  whiteText ? "text-white" : "text-gray"
                }`}
                field={subtitle}
                data-testid="header-sub-title"
              />
            )}
            {(body || isPageEditing) && (
              <RichTextWithTable
                tag="div"
                className={`${styles.body} richtext-content z-10 pl-[2px] mb-[20px] text-base leading-[1.5] tracking-[0.4px] m-0 font-normal pr-[var(--spacing-sixteen)] ${
                  whiteText ? "text-white" : "text-gray"
                }`}
                field={body}
                data-testid="header-body"
              />
            )}

            {(primaryCta?.value?.href ||
              secondaryCta?.value?.href ||
              isPageEditing) && (
              <div className="flex flex-wrap flex-col lg:flex-row ml-[10px] md:ml-[2px] mr-[10px] max-lg:items-start">
                {(primaryCta?.value?.href || isPageEditing) && (
                  <Button
                    key="header-cta-primary"
                    buttonType={
                      primaryCta?.value?.class ||
                      (whiteText ? "primary-reverse" : "primary")
                    }
                    className="lg:self-center md:!mb-5 mb-4!"
                    iconName="CHEVRON_BLUE_RIGHT"
                    field={primaryCta}
                    tabIndex={0}
                    aria-label={primaryCta?.value?.text}
                    data-testid="CTAButton"
                  />
                )}
                {(secondaryCta?.value?.href || isPageEditing) && (
                  <Button
                    key="header-cta-secondary"
                    buttonType={
                      secondaryCta?.value?.class ||
                      (whiteText ? "secondary-reverse" : "secondary")
                    }
                    className="lg:self-center md:!mb-5 mb-4!"
                    iconName="CHEVRON_BLUE_RIGHT"
                    field={secondaryCta}
                    tabIndex={0}
                    aria-label={secondaryCta?.value?.text}
                    data-testid="CTAButton2"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
